import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

/**
 * Login Page Component
 * Step 1: username + password
 * Step 2 (if user has 2FA): 6-digit code from authenticator (with email/SMS/recovery fallbacks)
 */
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [twoFA, setTwoFA] = useState(null); // {session_id, methods, masked_email, masked_phone}
  const [method, setMethod] = useState('totp');
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [otpSent, setOtpSent] = useState(null); // 'email' | 'sms' | null

  const { login, verify2FA, send2FAOtp } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result?.needs_2fa) {
        setTwoFA(result);
        setMethod('totp');
        setCode('');
        setOtpSent(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2FA({
        session_id: twoFA.session_id,
        code: code.trim(),
        remember_device: rememberDevice,
        method,
        username,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (channel) => {
    setLoading(true);
    try {
      await send2FAOtp({ session_id: twoFA.session_id, channel });
      setMethod(channel);
      setOtpSent(channel);
      toast.success(`Code sent via ${channel === 'sms' ? 'SMS' : 'email'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send ${channel} code`);
    } finally {
      setLoading(false);
    }
  };

  const cancelTwoFA = () => {
    setTwoFA(null);
    setCode('');
    setMethod('totp');
    setOtpSent(null);
  };

  // ============ 2FA PROMPT SCREEN ============
  if (twoFA) {
    const supports = twoFA.methods || [];
    const usingRecovery = method === 'recovery';
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <img src="/logo.jpg" alt="Total Data Recall" />
          </div>
          <h1 className="login-title">Two-Factor Authentication</h1>
          <h2 className="login-subtitle">
            {usingRecovery
              ? 'Enter one of your recovery codes'
              : (otpSent === 'email'
                ? `Code sent to ${twoFA.masked_email}`
                : otpSent === 'sms'
                  ? `Code sent to ${twoFA.masked_phone}`
                  : 'Enter the 6-digit code from your authenticator app')}
          </h2>

          <form onSubmit={handleVerify} className="login-form" data-testid="2fa-form">
            <div className="form-field">
              <Label htmlFor="code">{usingRecovery ? 'Recovery code' : 'Verification code'}</Label>
              <Input
                id="code"
                type="text"
                inputMode={usingRecovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={usingRecovery ? 'XXXX-XXXX' : '123 456'}
                required
                autoFocus
                data-testid="2fa-code-input"
                style={{ fontSize: '1.4rem', letterSpacing: usingRecovery ? '0.1em' : '0.3em', textAlign: 'center' }}
              />
            </div>

            <div className="form-field" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="remember-device"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                data-testid="remember-device-checkbox"
              />
              <Label htmlFor="remember-device" style={{ cursor: 'pointer', margin: 0, fontSize: '0.9rem' }}>
                Remember this device for 30 days
              </Label>
            </div>

            <Button type="submit" disabled={loading} className="login-button" data-testid="2fa-verify-btn">
              {loading ? 'Verifying...' : 'Verify & sign in'}
            </Button>
          </form>

          <div style={{ marginTop: '1.25rem', fontSize: '0.85rem', textAlign: 'center', color: '#64748b' }}>
            {!usingRecovery && (
              <div style={{ marginBottom: '0.5rem' }}>Can&apos;t access your authenticator?</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
              {supports.includes('email') && method !== 'email' && (
                <button type="button" onClick={() => handleSendOtp('email')} disabled={loading}
                  data-testid="2fa-use-email-btn"
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}>
                  Use email code
                </button>
              )}
              {supports.includes('sms') && method !== 'sms' && (
                <button type="button" onClick={() => handleSendOtp('sms')} disabled={loading}
                  data-testid="2fa-use-sms-btn"
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}>
                  Use SMS code
                </button>
              )}
              {!usingRecovery && supports.includes('recovery') && (
                <button type="button" onClick={() => { setMethod('recovery'); setCode(''); setOtpSent(null); }}
                  data-testid="2fa-use-recovery-btn"
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}>
                  Use a recovery code
                </button>
              )}
              {usingRecovery && (
                <button type="button" onClick={() => { setMethod('totp'); setCode(''); }}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}>
                  Back to authenticator code
                </button>
              )}
              <button type="button" onClick={cancelTwoFA}
                data-testid="2fa-cancel-btn"
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>
                Sign in as a different user
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ STANDARD LOGIN SCREEN ============
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <img src="/logo.jpg" alt="Total Data Recall" />
        </div>
        <h1 className="login-title">Total Data Recall</h1>
        <h2 className="login-subtitle">Job Management System</h2>

        <form onSubmit={handleLogin} className="login-form" data-testid="login-form">
          <div className="form-field">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              data-testid="username-input"
            />
          </div>

          <div className="form-field">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="password-input"
            />
          </div>

          <Button type="submit" disabled={loading} className="login-button" data-testid="login-submit-btn">
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
